/**
 * Find the index of the section containing currentBar (1-indexed bar position).
 * Returns -1 if no section contains the bar (e.g., gap between sections).
 *
 * Uses the same predicate as renderSections' is-active class to keep
 * render and tick paths consistent.
 *
 * @param {Array<{startBar,endBar,startBeat?,endBeat?}>} sections
 *   startBeat/endBeat are optional 1-based beats within the bar (default 1) for
 *   beat-precise section edges.
 * @param {number} currentBar
 * @returns {number} index in sections array, or -1
 */
import { matchSemantic, nextCycleColor } from './section-colors.js';

const DEFAULT_BEATS_PER_BAR = 4;

// KEEP IN SYNC with `sectionEdgeFracBar` in app/lib/bar-grid.mjs — any edit to
// the formula or defensive clamping here must be mirrored there (and vice versa).
//
// Fractional bar position of a section edge {bar, beat}. Local mirror of
// app/lib/bar-grid.mjs `sectionEdgeFracBar` — kept byte-for-byte consistent so
// render/tick agree with the app's frame-conversion path. `ui-primitives` is a
// dependency-free package, hence the duplication (no import from app/).
//
//   edgeFracBar(bar, beat, beatsPerBar) → bar + (beat - 1) / beatsPerBar
//
// Defensive: missing/invalid beat ⇒ 1; beat floored + clamped to 1..beatsPerBar;
// invalid beatsPerBar falls back to 4.
function edgeFracBar(bar, beat, beatsPerBar) {
  let bpb = Number(beatsPerBar);
  if (!Number.isFinite(bpb) || bpb < 1) bpb = DEFAULT_BEATS_PER_BAR;
  bpb = Math.floor(bpb);
  let b = Math.floor(Number(beat));
  if (!Number.isFinite(b)) b = 1;
  if (b < 1) b = 1;
  if (b > bpb) b = bpb;
  return bar + (b - 1) / bpb;
}

export function getActiveSectionIndex(sections, currentBar, beatsPerBar = DEFAULT_BEATS_PER_BAR) {
  if (!Array.isArray(sections) || !Number.isFinite(currentBar)) return -1;
  for (let i = 0; i < sections.length; i++) {
    const s = sections[i];
    const startFrac = edgeFracBar(s.startBar, s.startBeat, beatsPerBar);
    const endFrac   = edgeFracBar(s.endBar, s.endBeat, beatsPerBar);
    const startClamp = Math.max(1, startFrac);
    const endClamp   = Math.max(startClamp, endFrac);
    if (currentBar >= startClamp && currentBar < endClamp) return i;
  }
  return -1;
}

function el(tag, cls, text) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

/**
 * Build read-only section ribbon DOM elements (positioned by bar grid).
 *
 * Каждая секция несёт и `.rh-section-name`, и `.rh-section-letter` (первая буква,
 * aria-hidden) — какой из двух виден, решает CSS по ширине секции; полное имя
 * дублируется в `title`.
 *
 * @param {Array<{id,name,startBar,endBar,color,loopMode,startBeat?,endBeat?,guideState?}>} sections
 *   startBeat/endBeat are optional 1-based beats within the bar (default 1 =
 *   downbeat) for beat-precise section edges.
 *   guideState (Task 8b) is an optional spoken-guide marker, drawn as a button
 *   with the caller's icon (opts.guideIconSvg): 'on' → pressed (the cue will be
 *   heard), 'off' → drawn dimmed and struck through (so it can be turned back
 *   on), 'nophrase' → dashed + disabled (the voice pack has no phrase for this
 *   section name), absent → no button. Derived state, so the caller computes it
 *   (desktop: renderer/ui/rehearsal/section-guide-menu sectionGuideState) — this
 *   primitive stays dependency-free and only draws.
 * @param {number} totalBars  Total bars in song duration.
 * @param {number} currentBar 1-indexed bar position of playhead (for active highlight).
 * @param {number} beatsPerBar
 * @param {(frac:number)=>number} [posFn]  B-260625 — optional fractional-bar →
 *   0..1 timeline-position mapper. When provided, sections are positioned by it
 *   (time-based, honoring barOffset + exact bar interval) so they align with the
 *   bar grid. When omitted, falls back to the legacy `(frac-1)/totalBars` math
 *   byte-for-byte so existing callers/tests are unchanged.
 * @param {{guideIconSvg?: string, guideLabel?: (s: object) => string}} [opts]
 *   guideIconSvg — SVG markup for the guide button (the app injects it: this
 *   package has no dependencies). Without it no guide button is drawn.
 *   guideLabel — aria-label/tooltip text for the button, per section. REQUIRED
 *   for an accessible name whenever guideIconSvg is given: the button has only
 *   an icon, so without it screen readers announce an unnamed button.
 * @returns {HTMLDivElement[]} Array of <div class="rh-section [is-active]"> elements.
 */
export function renderSections(sections, totalBars, currentBar, beatsPerBar = DEFAULT_BEATS_PER_BAR, posFn = null, opts = {}) {
  if (!Array.isArray(sections) || sections.length === 0) return [];
  if (!Number.isFinite(totalBars) || totalBars <= 0) return [];

  const clamp01 = (x) => Math.max(0, Math.min(1, x));

  return sections.map((s, i) => {
    // Fractional bar edges (bar + beat). beat defaults to 1 (downbeat), so an
    // integer-only section yields frac == integer bar — backward compatible.
    const startFrac = edgeFracBar(s.startBar, s.startBeat, beatsPerBar);
    const endFrac   = edgeFracBar(s.endBar, s.endBeat, beatsPerBar);
    // Clamp to valid bar range. startBar is 1-indexed.
    const startClamp = Math.max(1, Math.min(totalBars + 1, startFrac));
    const endClamp   = Math.max(startClamp, Math.min(totalBars + 1, endFrac));
    let leftPct, widthPct;
    if (typeof posFn === 'function') {
      // B-260625 — time-based positioning matches the bar grid exactly.
      const left  = clamp01(posFn(startClamp)) * 100;
      const right = clamp01(posFn(endClamp)) * 100;
      leftPct  = left;
      widthPct = Math.max(0, right - left);
    } else {
      leftPct  = ((startClamp - 1) / totalBars) * 100;
      widthPct = ((endClamp - startClamp) / totalBars) * 100;
    }
    const isActive = currentBar >= startClamp && currentBar < endClamp;
    const looping = !!s.loopEnabled;

    let cls = 'rh-section';
    if (isActive) cls += ' is-active';
    if (looping)  cls += ' is-looping';
    const div = el('div', cls);
    div.style.left  = `${leftPct}%`;
    div.style.width = `${widthPct}%`;
    // Color resolution: explicit s.color → semantic by name → palette by index.
    // Desktop callers always pass s.color (set at mutation time), but the
    // fallback keeps the primitive safe for any consumer that omits it.
    const color = s.color || matchSemantic(s.name) || nextCycleColor(i);
    div.style.setProperty('--c', color);
    div.dataset.sectionId = s.id;
    div.dataset.startBar  = String(s.startBar);
    // Beat-aware fields (default 1) — desktop editor reads these as a fallback
    // for the beat-aware popover.
    div.dataset.startBeat = String(s.startBeat ?? 1);
    div.dataset.endBar    = String(s.endBar);
    div.dataset.endBeat   = String(s.endBeat ?? 1);
    div.appendChild(el('span', 'rh-section-name', s.name));
    // Узкая секция (Сергей 07.10): вместо имени — одна буква на том же тёмном бейдже.
    // Примитив отдаёт И имя, И букву, выбирает CSS по ширине секции (container query,
    // без измерений в JS). Буква — первый code point, а не charAt(0): иначе имя с
    // эмодзи в начале даёт половину суррогатной пары. aria-hidden — имя уже читается.
    const trimmedName = String(s.name ?? '').trim();
    const letterChar = (Array.from(trimmedName)[0] ?? '').toUpperCase();
    if (letterChar) {
      const letter = el('span', 'rh-section-letter', letterChar);
      letter.setAttribute('aria-hidden', 'true');
      div.appendChild(letter);
    }
    // Полное имя — в подсказке: на узкой секции видна только буква. Пустое/пробельное
    // имя подсказки не получает (иначе — пустой тултип).
    if (trimmedName) div.title = s.name;
    // Гайд секции — кликабельная иконка (Сергей 07.10, меняет решение 17.08
    // «off не рисует ничего»): выключенный гайд видно, чтобы включить его
    // обратно одним кликом. Иконку даёт приложение (пакет без зависимостей).
    if (opts?.guideIconSvg && (s.guideState === 'on' || s.guideState === 'off' || s.guideState === 'nophrase')) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `rh-section-guide-btn is-${s.guideState}`;
      // aria-pressed — только у переключателя (on/off). nophrase — статус: нажимать
      // нечего, и «не нажата» читалось бы как «можно нажать».
      if (s.guideState === 'nophrase') btn.disabled = true;
      else btn.setAttribute('aria-pressed', String(s.guideState === 'on'));
      if (opts.guideLabel) {
        const label = opts.guideLabel(s);
        btn.setAttribute('aria-label', label);
        btn.title = label;
      }
      btn.innerHTML = opts.guideIconSvg;
      div.appendChild(btn);
    }
    return div;
  });
}

/**
 * Render sections positioned by seconds (vs renderSections which positions by bars).
 * Used by web SPA which has startSec/endSec in manifest, not bar indices.
 *
 * @param {Array<{id,name,startSec,endSec,color}>} sections
 * @param {number} totalDurationSec
 * @param {number} currentSec  for active highlight
 * @returns {HTMLDivElement[]}
 */
export function renderSectionsBySec(sections, totalDurationSec, currentSec) {
  if (!Array.isArray(sections) || sections.length === 0) return [];
  if (!Number.isFinite(totalDurationSec) || totalDurationSec <= 0) return [];
  return sections.map((s, i) => {
    const div = document.createElement('div');
    div.className = 'rh-section';
    // `data-id` retained for backward-compat; `data-section-id` is the
    // canonical attribute used by section-handlers / loop-binding and
    // matches the desktop `renderSections` convention.
    div.dataset.id = s.id;
    div.dataset.sectionId = s.id;
    // Discoverability hint — rendered as a custom CSS tooltip via
    // `.rh-section[title]:hover::before` (see sections.css). Native title
    // attribute also acts as a fallback for screen readers / non-CSS hosts.
    div.title = 'Дабл-клик — залупить';
    const leftPct = (s.startSec / totalDurationSec) * 100;
    const widthPct = Math.max(0, ((s.endSec - s.startSec) / totalDurationSec) * 100);
    div.style.left = `${leftPct}%`;
    div.style.width = `${widthPct}%`;
    // Color resolution: explicit s.color → semantic match by name → cycle palette by index.
    // Mirrors desktop section-mutators which sets color at mutation time;
    // share manifests don't carry color, so we derive it at render time.
    // CSS var name must be --c to match desktop CSS (waves-column.css reads var(--c)).
    const color = s.color || matchSemantic(s.name) || nextCycleColor(i);
    div.style.setProperty('--c', color);
    if (Number.isFinite(currentSec) && currentSec >= s.startSec && currentSec < s.endSec) {
      div.classList.add('is-active');
    }
    // Nested .rh-section-name overlay (desktop CSS expects this child element
    // for the dark badge background + uppercase typography).
    const nameEl = document.createElement('div');
    nameEl.className = 'rh-section-name';
    nameEl.textContent = s.name || '';
    div.appendChild(nameEl);
    return div;
  });
}
